import { BACKEND_URL } from '@/core/config';
import { server } from '../core/server/axios';

export interface AddKassaVariables {
    name: string;
    code: string;
    address: string,
    adviser: string,
    depId: string | number //id филлиала
    kkmId: string;
    codeOneC: string;
    kkmUrl: string;
    nameOneC: string;
    pin: string;
}

export interface UpdateKassaVariables {
    id: string | number;
    payment: string;
    paymentPeriod: number;
}

export interface OpenKassaDayVariables {
    login: string;
    kassaId: string | number; //id кассы
}

export interface CashierModel {
    id: number
    login: string
    fullName: string
    position: string
    iin: string
    adress: string
    phone: string
    documents: any
}

export type OfdReconciliationSource = 'Заявка' | 'ОФД';
export type OfdReconciliationStatus = 'Совпало' | 'Нет у нас';
export type OfdActionType = 'buy' | 'sell';
export type OfdType = 'TICKET' | 'MONEY_PLACEMENT' | 'OPEN_SHIFT';

export interface OfdReconciliationRow {
    source: OfdReconciliationSource;
    status: OfdReconciliationStatus;
    ourId: number | null;
    ourDate: string | null;
    ourActionType: OfdActionType | null;
    ourSum: number | null;
    fiscalId: string | null;
    ofdId: string;
    ofdDate: string;
    ofdShiftNumber: number;
    ofdType: OfdType;
    ofdDescription: string;
    ofdSum: number;
}

export interface OfdReconciliationKassa {
    reportId: string;
    shiftOpen: string;
    shiftClose: string;
    depId: number;
    depName: string;
    kassaId: number;
    kassaCode: string;
    kkmId: string;
    transactionCount: number;
    totalSum: number;
    truncated: boolean;
    error: string | null;
    matchedCount: number;
    missingInOfdCount: number;
    extraInOfdCount: number;
    transactions: unknown[];
    rows: OfdReconciliationRow[];
}

export interface OfdReconciliationReport {
    fromDate: string;
    toDate: string;
    kassaCount: number;
    failedKassaCount: number;
    totalTransactions: number;
    matchedCount: number;
    missingInOfdCount: number;
    extraInOfdCount: number;
    kassas: OfdReconciliationKassa[];
}

/** Результат сверки смены с ОФД перед закрытием кассы */
export interface OfdShiftCheckReport {
    /** false — есть расхождения. Если сверку провести не удалось, приходит только `error`, без `ok` */
    ok?: boolean;
    error: string | null;
    /** Номера заявок, по которым чек в ОФД не сошёлся */
    requestMismatches?: number[] | null;
    /** Расхождения по внесениям и изъятиям наличности */
    cashMismatches?: { income: number; withdrawal: number } | null;
}

export class KassaController {
    async addKassa(request: AddKassaVariables) {
        return await server.post('/Cashbox/newCashbox', request, {
            baseURL: BACKEND_URL
        });
    }

    async updateKassa({ id, payment, paymentPeriod }: UpdateKassaVariables) {
        return await server.patch('/Cashbox/updateCashbox', { id, paymentPeriod, payment: `${payment}T00:00:00.000Z` }, {
            baseURL: BACKEND_URL
        });
    }

    async loadBalanceList(request: string | number) {
        return await server.get(`Cash/balanceList?depId=${request}`, {
            baseURL: BACKEND_URL
        });
    }

    async openKassaDay({ login, kassaId }: OpenKassaDayVariables) {
        return await server.post(`/CashierReport/openDay?login=${login}&kassaId=${kassaId}`, {}, {
            baseURL: BACKEND_URL
        });
    }

    async shiftKassaDay({ login, kassaId }: OpenKassaDayVariables) {
        return await server.get(`/CashierReport/shiftDay?login=${login}&reportId=${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async closeKassaDay({ login, kassaId }: OpenKassaDayVariables) {
        return await server.get(`/CashierReport/closeDay?login=${login}&kassaId=${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async balanceAct({ reportId, balanceDif }: { reportId: string, balanceDif: { cashId: number, factBalance: number, comment: string }[] }) {
        return await server.post(`/CashierReport/balanceAct?reportId=${reportId}`,
            balanceDif,
            {
                baseURL: BACKEND_URL
            });
    }

    async getBalanceAct(reportId: string): Promise<{ reportId: string, acts: { cashId: number, currencyCode: string, actualBalance: number, factBalance: number, balanceDif: number, comment: string }[] }> {
        return await server.get(`/CashierReport/balanceAct/${reportId}`, {
            baseURL: BACKEND_URL
        });
    }

    async checkKassaIsOpen({ kassaId }: { kassaId: string | number }) {
        return await server.get(`CashierReport/checkIsDayOpen/${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async getOfdLog({ requestId, actionType }: { requestId: number, actionType: string }) {
        return await server.get(`EcashCore/getLog/${requestId}/${actionType}`, {
            baseURL: BACKEND_URL
        });
    }

    async getZOfdLog({ businessKey }: { businessKey: string }) {
        return await server.get(`EcashCore/getZLog/${businessKey}`, {
            baseURL: BACKEND_URL
        });
    }

    async getLastZOfdLog(kassaId: string | number) {
        return await server.get(`EcashCore/zreport-last/${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async getXOfdLog(kassaId: string | number) {
        return await server.get(`EcashCore/xreport/${kassaId}`, {
            baseURL: BACKEND_URL
        });
    }

    async getCashiers(): Promise<CashierModel[]> {
        return await server.get(`api/Cashiers`, {
            baseURL: BACKEND_URL
        });
    }

    async getOofdReconciliation({ dateTo, dateFrom, kassaIds }: { dateTo: string, dateFrom: string, kassaIds: number[] }): Promise<OfdReconciliationReport> {
        return await server.post(`/Oofd/check`, {
            kassaIds,
            to: dateTo + 'T23:59:59.990Z',
            from: dateFrom + 'T00:00:00.000Z',
        }, {
            baseURL: BACKEND_URL
        });
    }

    async exportOofdReconciliation({ dateTo, dateFrom, kassaIds }: { dateTo: string, dateFrom: string, kassaIds: number[] }): Promise<Blob> {
        return await server.post(`/Oofd/check/export`, {
            kassaIds,
            to: dateTo + 'T23:59:59.990Z',
            from: dateFrom + 'T00:00:00.000Z',
        }, {
            responseType: "blob",
            baseURL: BACKEND_URL
        });
    }

    /** Сверка смены с ОФД перед закрытием кассы. Только читает, деньги и ОФД не трогает */
    async checkOofdReconciliation(reportId: string): Promise<OfdShiftCheckReport> {
        return await server.get(`/Oofd/reconciliation/closeDay/${reportId}`, {
            baseURL: BACKEND_URL
        });
    }
}